import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { classes } from "../styles";

interface UseSocialOfferDescriptionReturn {
  isOverflowing: boolean;
  maxHeight: string | number;
  estimatedLineCount: number;
  contentRef: React.RefObject<HTMLDivElement>;
  containerRef: React.RefObject<HTMLDivElement>;
}

export const useSocialOfferDescription = (): UseSocialOfferDescriptionReturn => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [maxHeight, setMaxHeight] = useState<string | number>("auto");
  const [estimatedLineCount, setEstimatedLineCount] = useState<number>(6); // Default to 6 lines
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate available space dynamically
  const calculateAvailableSpace = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    // Get parent container's dimensions
    const parentContainer = containerRef.current.closest(`.${classes.root}`);
    if (!parentContainer) {
      return;
    }

    const parentRect = parentContainer.getBoundingClientRect();

    // Calculate space taken by other elements in the parent
    const gap = 16;

    const otherElementsHeight = Array.from(parentContainer.children)
      .filter((child) => child !== containerRef.current)
      .reduce((total, child) => total + child.getBoundingClientRect().height + gap, 0);

    // Calculate available height for the description
    // Subtract other elements' height and some padding
    const padding = 32; // Additional padding/margin space

    const availableHeight = parentRect.height - otherElementsHeight - padding - 100;

    // Set a minimum height
    const minHeight = 100;
    const calculatedHeight = Math.max(availableHeight, minHeight);

    return calculatedHeight;
  }, []);

  // Calculate estimated number of lines that can fit in the available height
  const calculateEstimatedLineCount = useCallback(() => {
    if (!contentRef.current) {
      return 6; // Default fallback
    }

    // Get the line height of the content
    const computedStyle = window.getComputedStyle(contentRef.current);
    const lineHeight = Number.parseInt(computedStyle.lineHeight) || 20; // Default to 20px if can't determine

    // Calculate how many lines can fit in the available height
    const availableHeight = calculateAvailableSpace();
    if (!availableHeight) {
      return 6; // Default fallback
    }

    // Subtract some padding for safety
    const safetyPadding = 0;
    const safeHeight = availableHeight - safetyPadding;

    // Calculate and round down to ensure we don't overflow
    const lines = Math.floor(safeHeight / lineHeight);

    // Ensure at least 2 lines and at most 10 lines
    return Math.max(2, Math.min(lines, 30));
  }, [calculateAvailableSpace]);

  // Check if content overflows the container
  const checkOverflow = useCallback(() => {
    if (!contentRef.current) {
      return;
    }

    const element = contentRef.current;
    const isContentOverflowing = element.scrollHeight > element.clientHeight;
    setIsOverflowing(isContentOverflowing);

    const availableHeight = calculateAvailableSpace();
    if (availableHeight) {
      setMaxHeight(availableHeight);

      // Update estimated line count
      const lineCount = calculateEstimatedLineCount();
      setEstimatedLineCount(lineCount);
    }
  }, [calculateAvailableSpace, calculateEstimatedLineCount]);

  // Use layout effect to calculate dimensions before paint
  useLayoutEffect(() => {
    checkOverflow();

    // Add resize observer to recalculate on parent size changes
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);

      // Also observe the parent container if possible
      const parentContainer = containerRef.current.closest(`.${classes.root}`);
      if (parentContainer) {
        resizeObserver.observe(parentContainer);
      }
    }

    // Also listen for window resize
    window.addEventListener("resize", checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [checkOverflow]);

  return {
    isOverflowing,
    maxHeight,
    estimatedLineCount,
    contentRef,
    containerRef,
  };
};
